// Hook for managing customers
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import type { Customer, CustomerEntity, CustomerUser } from '@/types/customer'
import type { Database } from '@/types/database.generated'

type CustomerInsert = Database['public']['Tables']['customers']['Insert']
type CustomerUpdate = Database['public']['Tables']['customers']['Update']
type CustomerEntityInsert = Database['public']['Tables']['customer_entities']['Insert']
type CustomerEntityUpdate = Database['public']['Tables']['customer_entities']['Update']
type CustomerUserInsert = Database['public']['Tables']['customer_users']['Insert']
type CustomerUserUpdate = Database['public']['Tables']['customer_users']['Update']

export function useCustomers() {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    loadCustomers()
  }, [])

  const loadCustomers = async () => {
    try {
      setIsLoading(true)
      const { data, error: fetchError } = await supabase
        .from('customers')
        .select('*')
        .order('name')

      if (fetchError) throw fetchError
      setCustomers(data || [])
      setError(null)
    } catch (err) {
      setError(err as Error)
      console.error('Failed to load customers:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const createCustomer = async (customer: Omit<CustomerInsert, 'organization_id'>) => {
    // Get organization_id from authenticated user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data: userData } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single()

    if (!userData) throw new Error('User organization not found')

    const { data, error: createError } = await supabase
      .from('customers')
      .insert([{ ...customer, organization_id: userData.organization_id }])
      .select()
      .single()

    if (createError) throw createError
    await loadCustomers()
    return data
  }

  const updateCustomer = async (id: string, updates: CustomerUpdate) => {
    const { data, error: updateError } = await supabase
      .from('customers')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (updateError) throw updateError
    await loadCustomers()
    return data
  }

  const deleteCustomer = async (id: string) => {
    const { error: deleteError } = await supabase
      .from('customers')
      .delete()
      .eq('id', id)

    if (deleteError) throw deleteError
    await loadCustomers()
  }

  return {
    customers,
    isLoading,
    error,
    loadCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
  }
}

export function useCustomerEntities(customerId: string | null) {
  const [entities, setEntities] = useState<CustomerEntity[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (customerId) {
      loadEntities()
    } else {
      setEntities([])
    }
  }, [customerId])

  const loadEntities = async () => {
    if (!customerId) return

    try {
      setIsLoading(true)
      const { data, error: fetchError } = await supabase
        .from('customer_entities')
        .select('*')
        .eq('customer_id', customerId)
        .order('name')

      if (fetchError) throw fetchError
      setEntities((data as CustomerEntity[]) || [])
      setError(null)
    } catch (err) {
      setError(err as Error)
      console.error('Failed to load entities:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const createEntity = async (entity: Omit<CustomerEntityInsert, 'customer_id' | 'organization_id'>) => {
    if (!customerId) throw new Error('Customer ID is required')

    // Get organization_id from authenticated user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data: userData } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single()

    if (!userData) throw new Error('User organization not found')

    const { data, error: createError } = await supabase
      .from('customer_entities')
      .insert([{ ...entity, customer_id: customerId, organization_id: userData.organization_id }])
      .select()
      .single()

    if (createError) throw createError
    await loadEntities()
    return data as CustomerEntity
  }

  const updateEntity = async (id: string, updates: CustomerEntityUpdate) => {
    const { data, error: updateError } = await supabase
      .from('customer_entities')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (updateError) throw updateError
    await loadEntities()
    return data
  }

  const deleteEntity = async (id: string) => {
    const { error: deleteError } = await supabase
      .from('customer_entities')
      .delete()
      .eq('id', id)

    if (deleteError) throw deleteError
    await loadEntities()
  }

  return {
    entities,
    isLoading,
    error,
    loadEntities,
    createEntity,
    updateEntity,
    deleteEntity,
  }
}

export function useCustomerUsers(customerId: string | null) {
  const [users, setUsers] = useState<CustomerUser[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (customerId) {
      loadUsers()
    } else {
      setUsers([])
    }
  }, [customerId])

  const loadUsers = async () => {
    if (!customerId) return

    try {
      setIsLoading(true)
      const { data, error: fetchError } = await supabase
        .from('customer_users')
        .select('*')
        .eq('customer_id', customerId)
        .order('name')

      if (fetchError) throw fetchError
      setUsers((data as CustomerUser[]) || [])
      setError(null)
    } catch (err) {
      setError(err as Error)
      console.error('Failed to load users:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const createUser = async (user: Omit<CustomerUserInsert, 'customer_id' | 'organization_id'>) => {
    if (!customerId) throw new Error('Customer ID is required')

    // Get organization_id from authenticated user
    const { data: { user: authUser } } = await supabase.auth.getUser()
    if (!authUser) throw new Error('User not authenticated')

    const { data: userData } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', authUser.id)
      .single()

    if (!userData) throw new Error('User organization not found')

    const { data, error: createError } = await supabase
      .from('customer_users')
      .insert([{ ...user, customer_id: customerId, organization_id: userData.organization_id }])
      .select()
      .single()

    if (createError) throw createError
    await loadUsers()
    return data as CustomerUser
  }

  const updateUser = async (id: string, updates: CustomerUserUpdate) => {
    const { data, error: updateError } = await supabase
      .from('customer_users')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (updateError) throw updateError
    await loadUsers()
    return data
  }

  const deleteUser = async (id: string) => {
    const { error: deleteError } = await supabase
      .from('customer_users')
      .delete()
      .eq('id', id)

    if (deleteError) throw deleteError
    await loadUsers()
  }

  return {
    users,
    isLoading,
    error,
    loadUsers,
    createUser,
    updateUser,
    deleteUser,
  }
}
